"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.nspFilledUnserialToSerial = exports.buildConstructionFromNonSpecificWithFilled = exports.buildConstructionFromNonSpecific = void 0;
const web3_js_1 = require("@solana/web3.js");
const bn_js_1 = __importDefault(require("bn.js"));
const __1 = require("..");
const error_1 = require("../error");
const graph_utils_1 = require("../graph-utils");
const object_1 = require("../utils/object");
const tokens_1 = require("../utils/tokens");
const estimates_1 = require("./estimates");
/**
 * Get the set of previous actions for an action,
 */
const getPreviousActions = (actionIdx, actions, nextNodes) => {
    // First add indices to each action
    // Then remove the undefined actions
    // Then search if the action has the actionIdx
    return (actions
        .map((a, i) => {
        return a ? Object.assign(Object.assign({}, a), { idx: i }) : a;
    })
        .filter((a) => !!a)
        .map((a) => {
        const allIdxInNextNodes = [];
        // note this assumes that each action is only called once from a prior action
        // see https://github.com/Lev-Stambler/malloc-solana2/issues/5
        for (let i = 0; i < nextNodes[a.idx].length; i++) {
            const idxInNextNode = [-1, -1];
            idxInNextNode[1] = nextNodes[a.idx][i]
                .map((item) => item.actionIdx)
                .indexOf(actionIdx);
            if (idxInNextNode[1] !== -1) {
                idxInNextNode[0] = i;
                allIdxInNextNodes.push(idxInNextNode);
            }
        }
        if (allIdxInNextNodes.length === 0)
            return undefined;
        return {
            idx: a.idx,
            idxInNextNodes: allIdxInNextNodes,
        };
    })
        // Ensure that the previous action has the output mint
        .filter((i) => i &&
        actions[i.idx] &&
        i.idxInNextNodes.every((idxInNext) => actions[i.idx].tokenMintOuts[idxInNext[0]])));
};
/**
 * Get the mint from prior actions
 *
 * This assumes that the action given is not an initial action
 */
const getMintAccountsFromPriorActions = (actionIdx, actions, nextNodes) => {
    const previousActions = getPreviousActions(actionIdx, actions, nextNodes);
    const mints = previousActions
        .map((prev) => {
        const action = actions[prev.idx]; // assume its not undefined because getPreviousAction ensures its not undefined
        const mintsForPrevAction = prev.idxInNextNodes.map((idxInNextNodeInstance) => action.tokenMintOuts[idxInNextNodeInstance[0]]);
        return mintsForPrevAction;
    })
        .flat();
    return (0, tokens_1.removeDuplicateMints)(mints).map((pk) => new web3_js_1.PublicKey(pk));
};
const fillMintForNextNode = (nextNodesNoMint, outMints, allInputMints) => {
    if (nextNodesNoMint.length === 0)
        return [];
    if (nextNodesNoMint.length !== outMints.length) {
        throw `Expected the number of out mints to equal ${outMints.length}`;
    }
    return nextNodesNoMint.map((next, mintIdx) => {
        const mint = outMints[mintIdx];
        return next.map((n) => {
            const nextActionInputMints = allInputMints[n.actionIdx].map((p) => p.mint.toBase58());
            const nextInputMintIdx = nextActionInputMints.indexOf(mint.toBase58());
            if (nextInputMintIdx === -1) {
                console.log(outMints.map((o) => o.toBase58()));
                throw `Unexpected error: Expected to find mint ${mint.toBase58()} in the set of input mints for the next action with action index ${n.actionIdx}`;
            }
            return Object.assign(Object.assign({}, n), { nextInputMintIdx });
        });
    });
};
const getActionVertexInfoNoMinOuts = (nextNodes, tokenMintOuts, allInputMintAndTokenAccounts, inDegree, actionIdx) => {
    const nextNodesFilled = fillMintForNextNode(nextNodes, tokenMintOuts, allInputMintAndTokenAccounts);
    const vertexInfo = {
        inDegree,
        nextNodes: nextNodesFilled,
        numberInputMints: allInputMintAndTokenAccounts[actionIdx].length,
    };
    return vertexInfo;
};
/**
 * Fill in an isolated action to contain info on the next nodes to hit,
 * the minimum outputs, and output token accounts
 */
const isolatedActionToActionFilled = (actionIdx, isolatedAction, outputEstimates, inputEstimates, nonSpecificAction, allInputMintAndTokenAccounts, authority, vertexInfo, preferredTokenAccounts) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c;
    if (isolatedAction.tokenMintOuts.length !== outputEstimates.length)
        throw `Expected the number of token mints out to equal the number of estimate outs`;
    const tokenAccountOuts = yield Promise.all(isolatedAction.tokenMintOuts.map((mint) => (0, tokens_1.getTokenAccountUsedByMalloc)(authority, mint, preferredTokenAccounts)));
    const outputEstAmounts = outputEstimates.map((est) => est.amount);
    const minOuts = ((_a = nonSpecificAction.opts) === null || _a === void 0 ? void 0 : _a.minimumOutToEstimateRatio)
        ? getMinOutsFromEstimates(outputEstAmounts, nonSpecificAction.opts.minimumOutToEstimateRatio)
        : null;
    const vertexInfoFilled = vertexInfo;
    // Get the minimum outs
    vertexInfoFilled.minOuts = (_c = (_b = nonSpecificAction.overrides) === null || _b === void 0 ? void 0 : _b.minOuts) !== null && _c !== void 0 ? _c : minOuts;
    return Object.assign(Object.assign({}, isolatedAction), { actionVertexInfo: vertexInfoFilled, inputTokenAccounts: allInputMintAndTokenAccounts[actionIdx].map((inp) => inp.tokenAccount), tokenMintsIn: allInputMintAndTokenAccounts[actionIdx].map((inp) => inp.mint), actionTypeUID: nonSpecificAction.actionTypeUID, tokenAccountOuts, estimateOuts: outputEstimates, estimateIns: inputEstimates, nonSpecificAction });
});
const getMinOutsFromEstimates = (estimates, estimateMultiplier) => {
    if (estimates.length !== estimateMultiplier.length)
        throw `Expected the minimum output multipliers to be the same length as the number of output estimates`;
    return estimates.map((est, i) => new bn_js_1.default(est).mul(new bn_js_1.default(estimateMultiplier[i] * 1000)).divn(1000));
};
/**
 * Get the mints used for an action based off of past inputs and the init mints
 *
 * If an action has both a prior action and an init mint, it will add mints from both
 * while keeping the init mints in the beginning of the list to ensure that initing
 * still works properly (it relies on the init mints being the first amounts)
 */
const getMintAccounts = (actionIdx, isolatedActions, nextNodes, initActionIdxs, inMintAccounts) => {
    let mintAccounts = [];
    if (initActionIdxs.includes(actionIdx)) {
        mintAccounts.push(...inMintAccounts);
    }
    const mintsFromPrior = getMintAccountsFromPriorActions(actionIdx, isolatedActions, nextNodes);
    const mintsFromPriorFiltered = (0, tokens_1.removeMints)(mintsFromPrior, mintAccounts);
    mintAccounts.push(...mintsFromPriorFiltered);
    return mintAccounts;
};
const getIsolatedActionInfo = (actionIdxToId, order, initActionIdxs, nextNodes, inMints, construction, buildActionMap, provider, authority, config, preferredTokenAccounts) => __awaiter(void 0, void 0, void 0, function* () {
    const constructionFilled = (0, object_1.deepCloneObject)(construction);
    const isolatedActions = [
        ...Array(actionIdxToId.length).fill(undefined),
    ];
    const estimateOutFns = [
        ...Array(actionIdxToId.length).fill(undefined),
    ];
    const mintInsWithTokens = [
        ...Array(actionIdxToId.length).fill(undefined),
    ];
    let i = 0;
    for (let o = 0; o < order.length; o++) {
        const orderGroup = order[o];
        for (let q = 0; q < orderGroup.length; q++) {
            const actionIdx = orderGroup[q];
            const mintAccountsIn = getMintAccounts(actionIdx, isolatedActions, nextNodes, initActionIdxs, inMints);
            const actionData = (0, graph_utils_1.getNonSpecificActionDataFromId)(actionIdxToId[actionIdx], construction.actionDatas);
            // Update the filled construction
            actionData.inMints = [...mintAccountsIn];
            (0, graph_utils_1.updateNonSpecificActionDataFromId)(actionIdxToId[actionIdx], constructionFilled, actionData);
            const buildFN = buildActionMap[actionData.actionTypeUID].buildIsolatedAction;
            if (!buildFN)
                throw __1.errors.newBuilderError(`Could not find a builder function for action with UID ${actionData.actionTypeUID}`);
            // Get the token account which malloc will use
            const tokenWithMintAccounts = yield Promise.all(mintAccountsIn.map((mint) => __awaiter(void 0, void 0, void 0, function* () {
                return {
                    tokenAccount: yield (0, tokens_1.getTokenAccountUsedByMalloc)(authority, new web3_js_1.PublicKey(mint), preferredTokenAccounts),
                    mint,
                };
            })));
            mintInsWithTokens[actionIdx] = tokenWithMintAccounts;
            try {
                const { isolatedAction: _isolatedAction, estimateOuts } = yield buildFN({
                    inp: actionData.inputs,
                    inputTokens: tokenWithMintAccounts,
                    actionPID: new web3_js_1.PublicKey(actionData.actionPID),
                    provider,
                    authority,
                    config,
                    opts: {
                        preferredTokenAccounts: preferredTokenAccounts,
                    },
                });
                const isolatedAction = Object.assign(Object.assign({}, _isolatedAction), { nonSpecificAction: actionData, actionName: (0, graph_utils_1.prettyPrintActionId)(actionIdxToId[actionIdx]) });
                isolatedActions[actionIdx] = isolatedAction;
                estimateOutFns[actionIdx] = estimateOuts;
                // Update the filled action
                actionData.outMints = [...isolatedAction.tokenMintOuts];
                (0, graph_utils_1.updateNonSpecificActionDataFromId)(actionIdxToId[actionIdx], constructionFilled, actionData);
            }
            catch (e) {
                console.error(e);
                throw __1.errors.newActionBuilderError(actionData.actionTypeUID, (0, graph_utils_1.prettyPrintActionId)(actionIdxToId[actionIdx]), actionIdx, e, (0, exports.nspFilledUnserialToSerial)(constructionFilled));
            }
            i++;
        }
    }
    return {
        estimateOutFns: estimateOutFns,
        isolatedActions: isolatedActions,
        mintInsWithTokens: mintInsWithTokens,
        constructionFilled,
    };
});
const _buildConstructionFromNonSpecific = (construction, buildActionMap, provider, config, opts) => __awaiter(void 0, void 0, void 0, function* () {
    var _d;
    const initialMints = Object.keys(construction.source.mints);
    const amountsIn = Object.keys(construction.source.mints).map((mint) => construction.source.mints[mint]);
    const initialActions = Object.keys(construction.source.nextActions);
    const initialSplits = Object.values(construction.source.nextActions);
    const initMintAccounts = initialMints.map((p) => {
        try {
            return new web3_js_1.PublicKey(p);
        }
        catch (error) {
            throw (0, error_1.newBuilderError)(`The input mint, ${p}, is not a valid base-58 public key`);
        }
    });
    const authorityChecked = (_d = opts === null || opts === void 0 ? void 0 : opts.authority) !== null && _d !== void 0 ? _d : provider.wallet.publicKey;
    const { order, actionIdxToId, initActionIdxs, inDegrees, nextNodes } = (0, graph_utils_1.buildSeqListOfActionCalls)(construction.actionDatas, initialActions, initMintAccounts.length);
    const { isolatedActions, estimateOutFns, mintInsWithTokens, constructionFilled, } = yield getIsolatedActionInfo(actionIdxToId, order, initActionIdxs, nextNodes, initMintAccounts, construction, buildActionMap, provider, authorityChecked, config, opts === null || opts === void 0 ? void 0 : opts.preferredTokenAccounts);
    const vertexInfos = isolatedActions.map((action, actionIdx) => {
        try {
            return getActionVertexInfoNoMinOuts(nextNodes[actionIdx], action.tokenMintOuts, mintInsWithTokens, inDegrees[actionIdx], actionIdx);
        }
        catch (e) {
            console.error(e);
            throw __1.errors.newActionBuilderError(action.nonSpecificAction.actionTypeUID, (0, graph_utils_1.prettyPrintActionId)(actionIdxToId[actionIdx]), actionIdx, e, (0, exports.nspFilledUnserialToSerial)(constructionFilled));
        }
    });
    let estimateRet;
    try {
        estimateRet = yield (0, estimates_1.getConstructionEstimates)(order, isolatedActions, construction, initActionIdxs, initMintAccounts, vertexInfos, estimateOutFns, mintInsWithTokens);
    }
    catch (e) {
        if (e.mallocErrorMarker === "MALLOC ERROR MARKER") {
            throw Object.assign(Object.assign({}, e), { filledNonSpecificConstruction: (0, exports.nspFilledUnserialToSerial)(constructionFilled) });
        }
        else
            throw e;
    }
    const { estimateIns, estimateOuts } = estimateRet;
    // Update the estimates for the actions in the filled construction
    actionIdxToId.forEach((id, actionIdx) => {
        console.log("ID", id);
        const action = (0, graph_utils_1.getNonSpecificActionDataFromId)(id, constructionFilled.actionDatas);
        action.inEstimates = estimateIns[actionIdx];
        action.outEstimates = estimateOuts[actionIdx];
        console.log(action);
        (0, graph_utils_1.updateNonSpecificActionDataFromId)(id, constructionFilled, action);
    });
    const actions = yield Promise.all(isolatedActions.map((isolated, i) => __awaiter(void 0, void 0, void 0, function* () {
        const actionData = (0, graph_utils_1.getNonSpecificActionDataFromId)(actionIdxToId[i], construction.actionDatas);
        return yield isolatedActionToActionFilled(i, isolated, estimateOuts[i], estimateIns[i], actionData, mintInsWithTokens, authorityChecked, vertexInfos[i], opts === null || opts === void 0 ? void 0 : opts.preferredTokenAccounts);
    })));
    return {
        construction: {
            actions,
            actionOrders: order,
            initialActionIndices: initActionIdxs,
            initialSplits: initialSplits,
            amountIns: amountsIn.map((s) => new bn_js_1.default(s)),
        },
        filledNonSpecific: constructionFilled,
    };
});
// TODO: CLEAN UP AND TEST this function (estimate outs better)
// TODO: test the split in tokens
const buildConstructionFromNonSpecific = (construction, buildActionMap, provider, config, opts) => __awaiter(void 0, void 0, void 0, function* () {
    const { construction: constructionBuilt } = yield _buildConstructionFromNonSpecific(construction, buildActionMap, provider, config, opts);
    return constructionBuilt;
});
exports.buildConstructionFromNonSpecific = buildConstructionFromNonSpecific;
const buildConstructionFromNonSpecificWithFilled = (construction, buildActionMap, provider, config, opts) => __awaiter(void 0, void 0, void 0, function* () {
    const { construction: constructionBuilt, filledNonSpecific } = yield _buildConstructionFromNonSpecific(construction, buildActionMap, provider, config, opts);
    return {
        constructionBuilt,
        filledNonSpecific,
    };
});
exports.buildConstructionFromNonSpecificWithFilled = buildConstructionFromNonSpecificWithFilled;
const nspFilledUnserialToSerial = (filledUnserial) => {
    const actionDatasSerial = {};
    Object.keys(filledUnserial.actionDatas).forEach((k) => {
        const actionWrapped = filledUnserial.actionDatas[k];
        if ((0, __1.isNonSpecificActionGroup)(actionWrapped)) {
            const group = (0, __1.castToActionGroup)(actionWrapped);
            actionDatasSerial[k] = {
                actionGroup: Object.assign(Object.assign({}, group), { actions: Object.keys(group.actions).reduce((map, actionId) => {
                        map[actionId] = serializeActionFilled(group.actions[actionId]);
                        return map;
                    }, {}) }),
            };
        }
        else {
            actionDatasSerial[k] = {
                action: serializeActionFilled((0, __1.castToStandaloneAction)(filledUnserial.actionDatas[k])),
            };
        }
    });
    filledUnserial.actionDatas = actionDatasSerial;
    return Object.assign({}, filledUnserial);
};
exports.nspFilledUnserialToSerial = nspFilledUnserialToSerial;
const serializeActionFilled = (action) => {
    var _a, _b, _c, _d;
    return Object.assign(Object.assign({}, action), { inMints: (_a = action.inMints) === null || _a === void 0 ? void 0 : _a.map((m) => m.toString()), inEstimates: (_b = action.inEstimates) === null || _b === void 0 ? void 0 : _b.map(serializeMintWithAmount), outEstimates: (_c = action.outEstimates) === null || _c === void 0 ? void 0 : _c.map(serializeMintWithAmount), outMints: (_d = action.outMints) === null || _d === void 0 ? void 0 : _d.map((m) => m.toString()) });
};
function serializeMintWithAmount(m) {
    return {
        mint: m.mint.toString(),
        amount: m.amount.toString(),
    };
}
